import { useEffect, useState } from 'react';
import type { ClientSecurityQuestions, VerificationSettings } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import {
  PICK,
  offeredQuestions,
  togglePick,
  validatePicks,
  type Pick,
} from '../lib/securityQuestions.js';

/**
 * ehr#311 - the Demographics "Security Questions" section. The client picks and answers 2 of the
 * practice's questions in the client portal (Account & settings → Security questions, Chase's
 * CLIENT_SECURITY_QUESTIONS_10-08-V1). Here staff see whether they are set and when, can Clear
 * them, and (Jay 2026-10-12, "set new, never see") can set 2 new answers with a client who forgot.
 * Only a hash of each answer is kept: nobody, staff included, can see an answer, before or after.
 */

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function SecurityQuestionsSection({
  contactId,
  clientName,
}: {
  contactId: string;
  clientName: string;
}) {
  const [current, setCurrent] = useState<ClientSecurityQuestions | null>(null);
  const [settings, setSettings] = useState<VerificationSettings | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [show, setShow] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get<ClientSecurityQuestions>(`/contacts/${contactId}/security-questions`),
      api.get<VerificationSettings>('/practice/verification').catch(() => null),
    ])
      .then(([q, s]) => {
        if (!live) return;
        setCurrent(q);
        setSettings(s);
      })
      .catch((e) => live && setLoadErr(e instanceof ApiError ? e.message : 'Could not load'));
    return () => {
      live = false;
    };
  }, [contactId]);

  if (loadErr) return <p className="sq-err-msg">{loadErr}</p>;
  if (!current) return <p className="sq-intro">Loading…</p>;

  const who = clientName || 'The client';
  const methodOff = settings && !settings.methods.security_q.enabled;
  const isSet = current.questions.length > 0;
  const offered = offeredQuestions(current.offered.map((q) => q.id));
  const full = picks.length >= PICK;

  const startEditing = () => {
    setPicks([]);
    setErrs({});
    setShow({});
    setErr('');
    setConfirmClear(false);
    setEditing(true);
  };
  const stopEditing = () => {
    setPicks([]);
    setErrs({});
    setShow({});
    setEditing(false);
  };

  const setField = (id: string, key: 'answer' | 'confirm', v: string) => {
    setPicks((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: v } : p)));
    if (errs[id] || errs._) setErrs({});
  };

  const save = () => {
    const e = validatePicks(picks);
    setErrs(e);
    setErr('');
    if (Object.keys(e).length) return;
    setBusy(true);
    api
      .put<ClientSecurityQuestions>(`/contacts/${contactId}/security-questions`, {
        answers: picks.map((p) => ({ questionId: p.id, answer: p.answer })),
      })
      .then((q) => {
        setCurrent(q);
        stopEditing();
      })
      .catch((x) => setErr(x instanceof ApiError ? x.message : 'Could not save'))
      .finally(() => setBusy(false));
  };

  const clear = () => {
    setBusy(true);
    setErr('');
    api
      .del<ClientSecurityQuestions>(`/contacts/${contactId}/security-questions`)
      .then((q) => {
        setCurrent(q);
        setConfirmClear(false);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not clear'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="sq-tab">
      <p className="sq-intro">
        {who} sets these in the client portal (Account &amp; settings → Security questions). If they
        forgot them, you can set new ones here with them. Answers are stored scrambled: nobody,
        staff included, can see them.
      </p>
      {methodOff && (
        <p className="sq-off">
          Security questions are off for this practice (Data &amp; Settings → Client verification),
          so clients can’t use them to verify yet.
        </p>
      )}

      {!editing && !isSet && (
        <div className="sq-done">
          <p className="sq-stamp" id="sq-status">
            Not set yet. {who} hasn’t chosen security questions in the client portal.
          </p>
          <div className="sq-actions">
            <button type="button" className="btn primary" id="sq-set" onClick={startEditing}>
              Set answers
            </button>
          </div>
        </div>
      )}

      {!editing && isSet && (
        <div className="sq-done">
          <div className="sq-stamp" id="sq-status">
            Set{current.setAt ? ` on ${fmtDate(current.setAt)}` : ''}
            {current.setBy ? ` by ${current.setBy}` : ''}
          </div>
          <table className="sq-sum">
            <tbody>
              {current.questions.map((q) => (
                <tr key={q.id}>
                  <td>{q.text}</td>
                  <td className="sq-dots" aria-label="Answer hidden">
                    ••••••
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sq-actions">
            {confirmClear ? (
              <>
                <span className="sq-confirm">
                  Clear both answers? {who} can’t verify with security questions until new ones are
                  set, in the client portal or here.
                </span>
                <button type="button" className="btn" onClick={() => setConfirmClear(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn danger"
                  id="sq-clear-yes"
                  disabled={busy}
                  onClick={clear}
                >
                  Clear
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn"
                  id="sq-clear"
                  onClick={() => setConfirmClear(true)}
                >
                  Clear
                </button>
                <button type="button" className="btn primary" id="sq-set" onClick={startEditing}>
                  Set new answers
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="sq-pick" id="sq-editor">
          <p className="sq-intro">
            Confirm you’re speaking with {who} first, then let them choose the questions and say or
            type the answers. {isSet ? 'Saving replaces the current answers. ' : ''}Once saved,
            nobody can see the answers again.
          </p>
          <div className="sq-pick-hd">
            <span>Choose {PICK} questions</span>
            <span className={`sq-count${full ? ' full' : ''}`}>
              {picks.length} of {PICK} chosen
            </span>
          </div>
          <div className="sq-list">
            {offered.map((q) => {
              const idx = picks.findIndex((p) => p.id === q.id);
              const on = idx > -1;
              const p = picks[idx];
              return (
                <div
                  key={q.id}
                  className={`sq-q${on ? ' on' : !on && full ? ' off' : ''}`}
                  data-q={q.id}
                >
                  <button
                    type="button"
                    className="sq-q-head"
                    id={`sq-h-${q.id}`}
                    disabled={!on && full}
                    aria-pressed={on}
                    onClick={() => {
                      setPicks((ps) => togglePick(ps, q.id));
                      setErrs({});
                    }}
                  >
                    <span className="sq-num">{on ? idx + 1 : ''}</span>
                    <span className="sq-text">{q.text}</span>
                  </button>
                  {on && p && (
                    <div className="sq-body">
                      {(['answer', 'confirm'] as const).map((k) => {
                        const fid = `sq-${k}-${q.id}`;
                        return (
                          <div className="sq-field" key={k}>
                            <label htmlFor={fid}>
                              {k === 'answer' ? 'Answer' : 'Type it again'}
                            </label>
                            <input
                              id={fid}
                              type={show[fid] ? 'text' : 'password'}
                              autoComplete="off"
                              className={errs[q.id] ? 'bad' : ''}
                              value={p[k]}
                              onChange={(e) => setField(q.id, k, e.target.value)}
                            />
                            <button
                              type="button"
                              className="sq-peek"
                              onClick={() => setShow((s) => ({ ...s, [fid]: !s[fid] }))}
                            >
                              {show[fid] ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        );
                      })}
                      {errs[q.id] && <div className="sq-f-err">{errs[q.id]}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {errs._ && <p className="sq-err-msg">{errs._}</p>}
          <div className="sq-actions">
            <button type="button" className="btn" onClick={stopEditing}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              id="sq-save"
              disabled={busy}
              onClick={save}
            >
              {busy ? 'Saving…' : 'Save answers'}
            </button>
          </div>
        </div>
      )}
      {err && <p className="sq-err-msg">{err}</p>}
    </div>
  );
}
